import React, { useState } from 'react';

const TechnicalAdvantageSection = () => {
  const [activeTab, setActiveTab] = useState(1);
  
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  return (
    <>
      {/* TECHNICAL ADVANTAGE header - moved to top of page */}
      <div className="bg-gradient-to-b from-white to-indigo-50 pt-5 sm:pt-8 pb-2 relative border-b border-indigo-100">
        <div className="absolute top-0 right-0 w-24 h-24 sm:w-36 sm:h-36 bg-indigo-100 rounded-full opacity-30 -mt-8 -mr-8 z-0"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 sm:w-36 sm:h-36 bg-indigo-100 rounded-full opacity-20 -mb-8 -ml-8 z-0"></div>
        
        <div className="container mx-auto px-4 relative z-10 max-w-6xl">
          <div className="text-center">
            <div className="inline-flex items-center justify-center mb-4">
              <div className="h-1 w-12 bg-indigo-600 mr-4"></div>
              <span className="py-1 px-4 bg-indigo-100 text-indigo-600 font-bold text-sm rounded-full">TECHNICAL ADVANTAGE</span>
              <div className="h-1 w-12 bg-indigo-600 ml-4"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-5 text-gray-900 tracking-tight">What Sets HON-SONAR™ Apart</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-2">
              Key innovations that give you the professional edge in pattern-based trading
            </p>
            
            {/* Bottom accent */}
            <div className="flex justify-center mt-3 mb-2">
              <div className="h-1 w-16 sm:w-20 md:w-24 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Technical Advantage - Main Content Section */}
      <section className="py-10 sm:py-16 bg-white" id="advantage">
        <div className="container mx-auto px-4">
          {/* Enhanced container styles */}
          <div className="grid md:grid-cols-2 gap-10">
            <div className="tab-navigation rounded-xl overflow-hidden shadow-lg border border-indigo-100">
              <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 px-4 py-3 border-b border-indigo-200">
                <h3 className="font-bold text-indigo-800">Key Innovations</h3>
              </div>
              <div className="flex flex-col">
                {[
                  { id: 1, title: "Pattern Consensus Technology™", desc: "Unlike basic pattern indicators, HON-SONAR™ requires patterns to pass multiple validation criteria including volume confirmation, indicator alignment, and multi-timeframe validation." },
                  { id: 2, title: "Adaptive Risk Management", desc: "Position sizing automatically adjusts based on pattern reliability, historical performance, and current market volatility." },
                  { id: 3, title: "Market Regime Recognition", desc: "Automatically identifies and adapts to trending, ranging, corrective, and volatile market conditions." },
                  { id: 4, title: "Fibonacci Precision Entry", desc: "Strategically enters trades at optimal Fibonacci levels rather than standard entries, increasing potential reward while minimizing risk." },
                  { id: 5, title: "Black Swan Protection", desc: "Proprietary algorithms detect abnormal market conditions (extreme volatility, abnormal spreads) and pause trading to protect capital." },
                  { id: 6, title: "Performance Analytics", desc: "Continuous tracking of pattern performance allows the system to self-optimize over time, focusing on the most profitable patterns for current market conditions." }
                ].map(tab => (
                  <div 
                    key={tab.id}
                    className={`p-5 cursor-pointer transition-all relative ${activeTab === tab.id ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    {activeTab === tab.id && (
                      <div className="absolute top-0 bottom-0 left-0 w-1 bg-indigo-600"></div>
                    )}
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        {tab.id}
                      </div>
                      <h3 className={`font-bold text-lg ${activeTab === tab.id ? 'text-indigo-700' : 'text-gray-700'}`}>{tab.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="tab-content bg-white rounded-xl p-8 border border-gray-200 shadow-xl relative min-h-96 bg-gradient-to-br from-white to-indigo-50">
              <div className="absolute top-0 right-0 h-24 w-24 bg-indigo-100 rounded-full opacity-30 -mt-8 -mr-8 z-0"></div>
              <div className="absolute bottom-0 left-0 h-32 w-32 bg-indigo-100 rounded-full opacity-20 -mb-8 -ml-8 z-0"></div>
              <div className="relative z-10">
                {activeTab === 1 && (
                  <div className="h-full flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-indigo-700 mb-4">Pattern Consensus Technology™</h3>
                      <p className="text-gray-700 mb-6">Unlike basic pattern indicators, HON-SONAR™ requires patterns to pass multiple validation criteria including volume confirmation, indicator alignment, and multi-timeframe validation.</p>
                      
                      <div className="grid grid-cols-2 gap-4 my-6">
                        <div className="bg-white p-4 rounded-lg shadow-md border border-indigo-100 hover:shadow-lg transition transform hover:-translate-y-1">
                          <h4 className="font-semibold text-indigo-800 mb-2">Multi-Timeframe Validation</h4>
                          <p className="text-gray-600 text-sm">Confirms patterns across multiple timeframes to ensure robust signals</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-md border border-indigo-100 hover:shadow-lg transition transform hover:-translate-y-1">
                          <h4 className="font-semibold text-indigo-800 mb-2">Volume Confirmation</h4>
                          <p className="text-gray-600 text-sm">Validates patterns with corresponding volume behavior</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-md border border-indigo-100 hover:shadow-lg transition transform hover:-translate-y-1">
                          <h4 className="font-semibold text-indigo-800 mb-2">Indicator Alignment</h4>
                          <p className="text-gray-600 text-sm">Ensures technical indicators support the pattern direction</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-md border border-indigo-100 hover:shadow-lg transition transform hover:-translate-y-1">
                          <h4 className="font-semibold text-indigo-800 mb-2">Historical Performance</h4>
                          <p className="text-gray-600 text-sm">Analyzes past success rates of specific patterns</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-auto">
                      <div className="flex items-center justify-between p-4 bg-indigo-100 rounded-lg border border-indigo-200">
                        <div className="text-indigo-700 font-medium">Reduces false positives by up to 78%</div>
                        <svg className="h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeTab === 2 && (
                  <div className="h-full flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-indigo-700 mb-4">Adaptive Risk Management</h3>
                      <p className="text-gray-700 mb-6">Position sizing automatically adjusts based on pattern reliability, historical performance, and current market volatility.</p>
                      
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100 mb-6 shadow-md">
                        <h4 className="font-semibold text-blue-800 mb-2">Dynamic Risk Adjustment</h4>
                        <p className="text-blue-700">The system automatically adjusts position sizes based on:</p>
                        <ul className="mt-4 space-y-3 text-blue-700">
                          <li className="flex items-center">
                            <span className="w-6 h-6 bg-blue-500 rounded-full mr-3 flex items-center justify-center text-white text-xs">1</span>
                            Pattern reliability score
                          </li>
                          <li className="flex items-center">
                            <span className="w-6 h-6 bg-blue-500 rounded-full mr-3 flex items-center justify-center text-white text-xs">2</span>
                            Current market volatility
                          </li>
                          <li className="flex items-center">
                            <span className="w-6 h-6 bg-blue-500 rounded-full mr-3 flex items-center justify-center text-white text-xs">3</span>
                            Historical win rate for the pattern
                          </li>
                          <li className="flex items-center">
                            <span className="w-6 h-6 bg-blue-500 rounded-full mr-3 flex items-center justify-center text-white text-xs">4</span>
                            Account equity protection rules
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="mt-auto">
                      <div className="flex items-center justify-between p-4 bg-indigo-100 rounded-lg border border-indigo-200">
                        <div className="text-indigo-700 font-medium">Protects capital during uncertain market conditions</div>
                        <svg className="h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Enhanced tabs 3-6 */}
                {activeTab > 2 && (
                  <div className="h-full flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-indigo-700 mb-4">
                        {activeTab === 3 && "Market Regime Recognition"}
                        {activeTab === 4 && "Fibonacci Precision Entry"}
                        {activeTab === 5 && "Black Swan Protection"}
                        {activeTab === 6 && "Performance Analytics"}
                      </h3>
                      <p className="text-gray-700 mb-6">
                        {activeTab === 3 && "Automatically identifies and adapts to trending, ranging, corrective, and volatile market conditions."}
                        {activeTab === 4 && "Strategically enters trades at optimal Fibonacci levels rather than standard entries, increasing potential reward while minimizing risk."}
                        {activeTab === 5 && "Proprietary algorithms detect abnormal market conditions (extreme volatility, abnormal spreads) and pause trading to protect capital."}
                        {activeTab === 6 && "Continuous tracking of pattern performance allows the system to self-optimize over time, focusing on the most profitable patterns for current market conditions."}
                      </p>
                      
                      <div className="bg-white p-6 rounded-lg border border-indigo-100 mb-6 shadow-md">
                        <div className="flex items-center mb-4">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                            <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              {activeTab === 3 && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />}
                              {activeTab === 4 && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />}
                              {activeTab === 5 && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />}
                              {activeTab === 6 && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />}
                            </svg>
                          </div>
                          <h4 className="font-semibold text-indigo-800 text-lg">
                            {activeTab === 3 && "Advanced market regime detection technology"}
                            {activeTab === 4 && "Proprietary Fibonacci entry methodology"}
                            {activeTab === 5 && "Critical market event protection system"}
                            {activeTab === 6 && "Self-optimizing performance tracking"}
                          </h4>
                        </div>
                        <div className="pl-12">
                          <p className="text-gray-600">
                            {activeTab === 3 && "The system continuously analyzes market conditions to identify regime shifts and adjust trading parameters accordingly."}
                            {activeTab === 4 && "Rather than standard entries, HON-SONAR™ identifies key Fibonacci levels for optimal entry points with the best risk-to-reward ratios."}
                            {activeTab === 5 && "Proprietary algorithms constantly monitor for signs of abnormal market behavior to protect your capital during extreme events."}
                            {activeTab === 6 && "The system maintains a database of pattern performance across different market conditions to continuously optimize its approach."}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-auto">
                      <div className="flex items-center justify-between p-4 bg-indigo-100 rounded-lg border border-indigo-200">
                        <div className="text-indigo-700 font-medium">
                          {activeTab === 3 && "Optimizes performance in any market condition"}
                          {activeTab === 4 && "Improves risk-to-reward ratios by 35-50%"}
                          {activeTab === 5 && "Prevented losses during major market events"}
                          {activeTab === 6 && "Continuously improves with every trade"}
                        </div>
                        <svg className="h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TechnicalAdvantageSection;